import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatNumber(num: number, decimals: number = 0): string {
  if (num === null || num === undefined || isNaN(num)) {
    return '0'
  }

  // Always show whole numbers only - no decimals in UI
  const wholeNumber = Math.round(num)

  return wholeNumber.toLocaleString('en-US')
}

export function formatDuration(seconds: number): string {
  if (seconds === null || seconds === undefined) return '-'
  if (!seconds || seconds < 0) return '0s'

  // Always round to whole seconds - no decimals
  const totalSeconds = Math.round(seconds)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`
  } else {
    return `${secs}s`
  }
}

export function formatPercentage(value: number, decimals: number = 0): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%'
  }

  // Always show whole percentages - no decimals
  const wholePercent = Math.round(value)
  return `${wholePercent}%`
}

export function formatDate(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export function formatDateTime(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export function getTargetStatus(
  value: number,
  target: number,
  isLowerBetter: boolean = false
): 'success' | 'warning' | 'danger' {
  if (isLowerBetter) {
    if (value <= target) return 'success'
    if (value <= target * 1.2) return 'warning'
    return 'danger'
  } else {
    if (value >= target) return 'success'
    if (value >= target * 0.8) return 'warning'
    return 'danger'
  }
}

export function calculateTrend(
  current: number,
  previous: number
): {
  direction: 'up' | 'down' | 'stable'
  percentage: number
} {
  if (previous === 0) {
    return { direction: 'stable', percentage: 0 }
  }

  const change = ((current - previous) / previous) * 100

  if (Math.abs(change) < 0.1) {
    return { direction: 'stable', percentage: 0 }
  }

  return {
    direction: change > 0 ? 'up' : 'down',
    percentage: Math.abs(change)
  }
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout

  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

/**
 * Format phone number to show +country code (area code) format
 * Removes last 7 digits, leaving country code + area code
 * @param phoneNumber - Full phone number string
 * @returns Formatted string like "+1 (555)" or original if invalid
 */
export function formatAreaCode(phoneNumber: string): string {
  if (!phoneNumber || phoneNumber.length < 10) {
    return phoneNumber
  }

  // Remove last 7 digits
  const withoutLast7 = phoneNumber.slice(0, -7)

  if (withoutLast7.length < 4) {
    return phoneNumber
  }

  // Extract country code and area code
  // Assume area code is always the last 3 digits of what remains
  const areaCode = withoutLast7.slice(-3)
  const countryCode = withoutLast7.slice(0, -3)

  // Format as +country code (area code)
  return `+${countryCode} (${areaCode})`
}

/**
 * Smart date range analysis and chart scaling
 */
export interface DateRangeInfo {
  startDate: Date
  endDate: Date
  totalDays: number
  totalMonths: number
  totalYears: number
  useQuarters: boolean
  useMonths: boolean
  useDays: boolean
}

/**
 * Analyze date range to determine optimal chart scaling
 */
export function analyzeDateRange(
  fromDate: string,
  toDate: string
): DateRangeInfo {
  // Use explicit date construction to avoid timezone conversion issues
  const [fromYear, fromMonth, fromDay] = fromDate
    .split('-')
    .map(num => parseInt(num))
  const [toYear, toMonth, toDay] = toDate.split('-').map(num => parseInt(num))
  const startDate = new Date(fromYear, fromMonth - 1, fromDay) // month is 0-indexed
  const endDate = new Date(toYear, toMonth - 1, toDay)

  const diffTime = Math.abs(endDate.getTime() - startDate.getTime())
  const totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  const totalMonths = Math.round(totalDays / 30.44) // Average days per month
  const totalYears = totalDays / 365.25

  // Decision logic for chart scaling:
  // > 24 months (2 years): Use quarters (2025 Q1, Q2, Q3, Q4)
  // 3-24 months: Use months (Jan 2025, Feb 2025, etc.)
  // < 3 months: Use days (individual dates)

  const useQuarters = totalMonths > 24
  const useMonths = totalMonths >= 3 && totalMonths <= 24
  const useDays = totalMonths < 3

  return {
    startDate,
    endDate,
    totalDays,
    totalMonths,
    totalYears,
    useQuarters,
    useMonths,
    useDays
  }
}

/**
 * Generate smart date timeline based on range analysis
 */
export function generateSmartDateTimeline(
  fromDate: string,
  toDate: string
): Array<{ date: string; displayDate: string }> {
  const rangeInfo = analyzeDateRange(fromDate, toDate)
  const timeline: Array<{ date: string; displayDate: string }> = []

  if (rangeInfo.useQuarters) {
    // Generate quarterly timeline
    const startYear = rangeInfo.startDate.getFullYear()
    const endYear = rangeInfo.endDate.getFullYear()
    const startQuarter = Math.floor(rangeInfo.startDate.getMonth() / 3) + 1
    const endQuarter = Math.floor(rangeInfo.endDate.getMonth() / 3) + 1

    for (let year = startYear; year <= endYear; year++) {
      const firstQ = year === startYear ? startQuarter : 1
      const lastQ = year === endYear ? endQuarter : 4

      for (let quarter = firstQ; quarter <= lastQ; quarter++) {
        const quarterStartMonth = (quarter - 1) * 3
        const quarterDate = new Date(year, quarterStartMonth, 1)
        const y = quarterDate.getFullYear()
        const m = String(quarterDate.getMonth() + 1).padStart(2, '0')
        const da = String(quarterDate.getDate()).padStart(2, '0')
        timeline.push({
          date: `${y}-${m}-${da}`,
          displayDate: `${year} Q${quarter}`
        })
      }
    }
  } else if (rangeInfo.useMonths) {
    // Generate monthly timeline
    const current = new Date(
      rangeInfo.startDate.getFullYear(),
      rangeInfo.startDate.getMonth(),
      1
    )
    const todayLocal = new Date()
    todayLocal.setHours(0, 0, 0, 0)
    const endRef = new Date(
      Math.min(rangeInfo.endDate.getTime(), todayLocal.getTime())
    )
    const end = new Date(endRef.getFullYear(), endRef.getMonth(), 1)

    while (current <= end) {
      const y = current.getFullYear()
      const m = String(current.getMonth() + 1).padStart(2, '0')
      const da = String(current.getDate()).padStart(2, '0')
      timeline.push({
        date: `${y}-${m}-${da}`,
        displayDate: current.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short'
        })
      })
      current.setMonth(current.getMonth() + 1)
    }
  } else {
    // Generate daily timeline (existing logic)
    const todayLocal2 = new Date()
    todayLocal2.setHours(0, 0, 0, 0)
    const endDaily = new Date(
      Math.min(rangeInfo.endDate.getTime(), todayLocal2.getTime())
    )
    const current = new Date(rangeInfo.startDate)

    while (current <= endDaily) {
      const y = current.getFullYear()
      const m = String(current.getMonth() + 1).padStart(2, '0')
      const da = String(current.getDate()).padStart(2, '0')
      timeline.push({
        date: `${y}-${m}-${da}`,
        displayDate: current.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })
      })
      current.setDate(current.getDate() + 1)
    }
  }

  console.log(
    `📅 Smart timeline: ${timeline.length} data points (${rangeInfo.useQuarters ? 'quarterly' : rangeInfo.useMonths ? 'monthly' : 'daily'})`
  )
  return timeline
}

/**
 * Format x-axis tick labels based on date range
 */
export function formatSmartXAxisTick(
  tickItem: string,
  rangeInfo: DateRangeInfo
): string {
  // Parse YYYY-MM-DD as local date to avoid UTC shift
  const [yy, mm, dd] = tickItem.split('-').map(num => parseInt(num))
  const date = new Date(yy, (mm || 1) - 1, dd || 1)

  if (rangeInfo.useQuarters) {
    const quarter = Math.floor(date.getMonth() / 3) + 1
    return `${date.getFullYear()} Q${quarter}`
  } else if (rangeInfo.useMonths) {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short'
    })
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  }
}

/**
 * Process tickets data with smart date grouping
 */
export function processTicketsWithSmartGrouping(
  tickets: any[],
  fromDate: string,
  toDate: string,
  groupBy: string
): { chartData: any[]; rangeInfo: DateRangeInfo } {
  // 🔍 DEBUG: Chart processing analysis
  console.log('📊 [DEBUG] processTicketsWithSmartGrouping input:', {
    ticketCount: tickets.length,
    fromDate,
    toDate,
    groupBy,
    sampleTicketDates: tickets.slice(0, 5).map(t => t.tickets_createdAt)
  })

  const rangeInfo = analyzeDateRange(fromDate, toDate)
  const timeline = generateSmartDateTimeline(fromDate, toDate)

  // 🔍 DEBUG: Timeline analysis
  console.log('📊 [DEBUG] Timeline generated:', {
    rangeInfo,
    timelineLength: timeline.length,
    firstTimelineDate: timeline[0],
    lastTimelineDate: timeline[timeline.length - 1],
    sampleTimeline: timeline.slice(0, 5)
  })

  // Group tickets by the smart timeline periods
  const ticketsByPeriod: Record<string, any[]> = {}

  tickets.forEach(ticket => {
    // Parse ticket date as local day
    const parsed = new Date(ticket.tickets_createdAt)
    const ticketDate = new Date(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate()
    )
    let periodKey = ''

    if (rangeInfo.useQuarters) {
      const y = ticketDate.getFullYear()
      const quarter = Math.floor(ticketDate.getMonth() / 3) + 1
      const quarterStart = new Date(y, (quarter - 1) * 3, 1)
      periodKey = `${quarterStart.getFullYear()}-${String(quarterStart.getMonth() + 1).padStart(2, '0')}-01`
    } else if (rangeInfo.useMonths) {
      const ms = new Date(ticketDate.getFullYear(), ticketDate.getMonth(), 1)
      periodKey = `${ms.getFullYear()}-${String(ms.getMonth() + 1).padStart(2, '0')}-01`
    } else {
      periodKey = `${ticketDate.getFullYear()}-${String(ticketDate.getMonth() + 1).padStart(2, '0')}-${String(ticketDate.getDate()).padStart(2, '0')}`
    }

    if (!ticketsByPeriod[periodKey]) ticketsByPeriod[periodKey] = []
    ticketsByPeriod[periodKey].push(ticket)
  })

  // Get all possible group keys
  const allGroupKeys = new Set<string>()
  tickets.forEach(ticket => {
    let groupKey = 'Unknown'

    switch (groupBy) {
      case 'model':
        groupKey = ticket.custom_model_number || 'Unknown Model'
        break
      case 'type':
        groupKey = ticket.custom_type || 'Unknown Type'
        break
      case 'problem':
        groupKey = ticket.custom_problem_code || 'Unknown Problem'
        break
      case 'client':
        groupKey = ticket.custom_client || 'Unknown Client'
        break
      case 'status':
        groupKey = ticket.tickets_status || 'Unknown Status'
        break
      default:
        groupKey = 'Total'
    }

    allGroupKeys.add(groupKey)
  })

  // First pass: determine which group keys actually have data
  const groupKeysWithData = new Set<string>()
  timeline.forEach(({ date }) => {
    const periodTickets = ticketsByPeriod[date] || []
    periodTickets.forEach(ticket => {
      let groupKey = 'Unknown'

      switch (groupBy) {
        case 'model':
          groupKey = ticket.custom_model_number || 'Unknown Model'
          break
        case 'type':
          groupKey = ticket.custom_type || 'Unknown Type'
          break
        case 'problem':
          groupKey = ticket.custom_problem_code || 'Unknown Problem'
          break
        case 'client':
          groupKey = ticket.custom_client || 'Unknown Client'
          break
        case 'status':
          groupKey = ticket.tickets_status || 'Unknown Status'
          break
        default:
          groupKey = 'Total'
      }

      groupKeysWithData.add(groupKey)
    })
  })

  console.log(
    '📊 [DEBUG] Group keys with actual data:',
    Array.from(groupKeysWithData)
  )

  // Create chart data points - ONLY include series that have data
  const chartData = timeline.map(({ date, displayDate }) => {
    const dataPoint: any = { date }
    const periodTickets = ticketsByPeriod[date] || []

    // Initialize only group keys that have data somewhere in the dataset
    groupKeysWithData.forEach(groupKey => {
      dataPoint[groupKey] = 0
    })

    // Count tickets for this period
    periodTickets.forEach(ticket => {
      let groupKey = 'Unknown'

      switch (groupBy) {
        case 'model':
          groupKey = ticket.custom_model_number || 'Unknown Model'
          break
        case 'type':
          groupKey = ticket.custom_type || 'Unknown Type'
          break
        case 'problem':
          groupKey = ticket.custom_problem_code || 'Unknown Problem'
          break
        case 'client':
          groupKey = ticket.custom_client || 'Unknown Client'
          break
        case 'status':
          groupKey = ticket.tickets_status || 'Unknown Status'
          break
        default:
          groupKey = 'Total'
      }

      if (groupKeysWithData.has(groupKey)) {
        dataPoint[groupKey] = (dataPoint[groupKey] || 0) + 1
      }
    })

    return dataPoint
  })

  // 🔍 DEBUG: Final chart data analysis
  const nonZeroSeries = new Set<string>()
  chartData.forEach(point => {
    Object.keys(point).forEach(key => {
      if (key !== 'date' && point[key] > 0) {
        nonZeroSeries.add(key)
      }
    })
  })

  console.log('📊 [DEBUG] Final chart data:', {
    chartDataPoints: chartData.length,
    totalSeriesInData: groupKeysWithData.size,
    seriesWithNonZeroValuesCount: nonZeroSeries.size,
    allSeriesKeys: Array.from(groupKeysWithData),
    seriesWithNonZeroValues: Array.from(nonZeroSeries),
    firstDataPoint: chartData[0],
    lastDataPoint: chartData[chartData.length - 1],
    dateRangeInChart:
      chartData.length > 0
        ? `${chartData[0].date} to ${chartData[chartData.length - 1].date}`
        : 'No data'
  })

  return { chartData, rangeInfo }
}
